<template>
    <q-card flat bordered class="q-mb-sm field-editor-card">
        <q-card-section class="q-pa-sm">
            <div class="row q-gutter-sm items-start">
                <!-- Drag handle -->
                <div class="col-auto self-center cursor-move" style="cursor: grab;">
                    <q-icon name="drag_indicator" color="grey-6" size="sm" />
                </div>

                <!-- Field type selector -->
                <div class="col-2">
                    <q-select
                        v-model="localField.typeId"
                        :options="fieldTypeOptions"
                        label="Type"
                        dense
                        filled
                        emit-value
                        map-options
                        @update:model-value="onFieldChanged"
                    />
                </div>

                <!-- Label -->
                <div class="col">
                    <q-input
                        v-model="localField.label"
                        label="Label"
                        dense
                        filled
                        @update:model-value="onFieldChanged"
                    />
                </div>

                <!-- Required toggle -->
                <div class="col-auto self-center">
                    <q-toggle
                        v-model="localField.required"
                        label="Required"
                        dense
                        size="sm"
                        @update:model-value="onFieldChanged"
                    />
                </div>

                <!-- Allow opt-out toggle -->
                <div class="col-auto self-center">
                    <q-toggle
                        v-model="localField.allowOptOut"
                        label="Can opt out"
                        dense
                        size="sm"
                        @update:model-value="onFieldChanged"
                    />
                </div>

                <!-- Expand/collapse options -->
                <div class="col-auto self-center">
                    <q-btn
                        flat
                        dense
                        round
                        :icon="expanded ? 'expand_less' : 'expand_more'"
                        @click="expanded = !expanded"
                        size="sm"
                    />
                </div>

                <!-- Move up/down and delete -->
                <div class="col-auto self-center">
                    <q-btn
                        flat dense round icon="arrow_upward" size="sm"
                        :disable="isFirst"
                        @click="$emit('move-up')"
                    />
                    <q-btn
                        flat dense round icon="arrow_downward" size="sm"
                        :disable="isLast"
                        @click="$emit('move-down')"
                    />
                    <q-btn
                        flat dense round icon="delete" color="negative" size="sm"
                        @click="$emit('remove')"
                    />
                </div>

            </div>

            <!-- Expanded options section -->
            <q-slide-transition>
                <div v-show="expanded" class="q-mt-sm q-ml-xl">
                    <!-- Sublabel -->
                    <q-input
                        v-model="localField.sublabel"
                        label="Sublabel / caption"
                        dense
                        filled
                        class="q-mb-sm"
                        @update:model-value="onFieldChanged"
                    />

                    <q-select v-if="localField.typeId === 'DURATION'" v-model="localField.options.units"
                        :options="['minute','hour','day','week','month','year'].map(value => ({ value, label: value + '(s)' }))"
                        multiple emit-value map-options filled dense label="Allowed duration units" class="q-mb-sm" @update:model-value="onFieldChanged" />
                    <!-- Custom Field ID -->
                    <q-input
                        v-model="localField.id"
                        label="Field ID (auto-generated if empty)"
                        dense
                        filled
                        class="q-mb-sm"
                        @update:model-value="onFieldChanged"
                    />

                    <!-- Is Status toggle -->
                    <q-toggle
                        v-model="localField.isStatus"
                        label="Sets form response status"
                        dense
                        size="sm"
                        class="q-mb-sm"
                        @update:model-value="onFieldChanged"
                    />

                    <!-- Type-specific option editors -->
                    <div class="text-subtitle2 q-mb-xs">Type Options</div>

                    <!-- DISPLAY_TEXT options -->
                    <div v-if="localField.typeId === 'DISPLAY_TEXT'" class="q-gutter-sm">
                        <q-input v-model="localField.options.title" label="Title" dense filled @update:model-value="onFieldChanged" />
                        <q-input v-model="localField.options.subtitle" label="Subtitle" dense filled @update:model-value="onFieldChanged" />
                        <q-input v-model="localField.options.body" label="Body text" dense filled type="textarea" autogrow @update:model-value="onFieldChanged" />
                    </div>

                    <!-- TEXT options -->
                    <div v-else-if="localField.typeId === 'TEXT'" class="q-gutter-sm">
                        <q-input v-model="localField.options.placeholder" label="Placeholder" dense filled @update:model-value="onFieldChanged" />
                        <q-input v-model.number="localField.options.minlines" label="Min lines (multiline)" dense filled type="number" @update:model-value="onFieldChanged" />
                        <q-input v-model.number="localField.options.maxLength" label="Max length" dense filled type="number" @update:model-value="onFieldChanged" />
                        <q-toggle v-model="localField.options.inline" label="Inline layout" dense size="sm" @update:model-value="onFieldChanged" />
                    </div>

                    <!-- SELECT / RADIO options (incl. demographic SELECT_* variants) -->
                    <div v-else-if="baseTypeId === 'SELECT' || baseTypeId === 'RADIO'" class="q-gutter-sm">
                        <q-select
                            v-model="selectPreset"
                            :options="selectPresetOptions"
                            label="Preset options"
                            dense
                            filled
                            emit-value
                            map-options
                            @update:model-value="onSelectPresetChanged"
                        />
                        <div v-if="selectPreset === 'custom'">
                            <div class="text-caption q-mb-xs">Custom options (one per line, or "value|label" format):</div>
                            <q-input
                                v-model="customOptionsText"
                                type="textarea"
                                dense
                                filled
                                autogrow
                                placeholder="Option 1&#10;value|Label Text&#10;Option 3"
                                @update:model-value="onCustomOptionsChanged"
                            />
                        </div>
                        <q-toggle v-model="localField.options.horizontal" label="Horizontal layout" dense size="sm" @update:model-value="onFieldChanged" />
                        <q-toggle v-if="baseTypeId === 'SELECT'" v-model="localField.options.multiple" label="Allow multiple selections" dense size="sm" @update:model-value="onFieldChanged" />
                        <q-input v-model="localField.options.placeholder" label="Placeholder text" dense filled @update:model-value="onFieldChanged" />
                    </div>

                    <!-- DATE / DATETIME options -->
                    <div v-else-if="baseTypeId === 'DATE'" class="q-gutter-sm">
                        <q-toggle v-model="localField.options.allowFuture" label="Allow future dates" dense size="sm" @update:model-value="onFieldChanged" />
                        <q-toggle v-model="localField.options.allowPast" label="Allow past dates" dense size="sm" @update:model-value="onFieldChanged" />
                        <q-toggle v-model="localField.options.inline" label="Inline layout" dense size="sm" @update:model-value="onFieldChanged" />
                        <div v-if="localField.typeId === 'DATETIME'" class="row q-gutter-sm">
                            <q-input v-model.number="localField.options.step" label="Minute step" dense filled type="number" class="col" @update:model-value="onFieldChanged" />
                            <q-input v-model="localField.options.minTime" label="Min time (HH:mm)" dense filled class="col" @update:model-value="onFieldChanged" />
                            <q-input v-model="localField.options.maxTime" label="Max time (HH:mm)" dense filled class="col" @update:model-value="onFieldChanged" />
                        </div>
                    </div>

                    <!-- SCALE options -->
                    <div v-else-if="localField.typeId === 'SCALE'" class="q-gutter-sm">
                        <div class="row q-gutter-sm">
                            <q-input v-model.number="localField.options.min" label="Min" dense filled type="number" class="col" @update:model-value="onFieldChanged" />
                            <q-input v-model.number="localField.options.max" label="Max" dense filled type="number" class="col" @update:model-value="onFieldChanged" />
                            <q-input v-model.number="localField.options.step" label="Step" dense filled type="number" class="col" @update:model-value="onFieldChanged" />
                        </div>
                        <q-input v-model="localField.options.startLabel" label="Start label" dense filled @update:model-value="onFieldChanged" />
                        <q-input v-model="localField.options.endLabel" label="End label" dense filled @update:model-value="onFieldChanged" />
                        <q-select
                            v-model="localField.options.emoji"
                            :options="emojiOptions"
                            label="Emoji icon"
                            dense
                            filled
                            clearable
                            emit-value
                            map-options
                            @update:model-value="onFieldChanged"
                        />
                    </div>

                    <!-- STAR_RATING options -->
                    <div v-else-if="localField.typeId === 'STAR_RATING'" class="q-gutter-sm">
                        <q-input v-model.number="localField.options.count" label="Star count" dense filled type="number" @update:model-value="onFieldChanged" />
                        <q-toggle v-model="localField.options.allowHalf" label="Allow half stars" dense size="sm" @update:model-value="onFieldChanged" />
                    </div>

                    <!-- CHECKLIST options -->
                    <div v-else-if="localField.typeId === 'CHECKLIST'" class="q-gutter-sm">
                        <q-toggle v-model="localField.options.block" label="Show block-wide checkbox" dense size="sm" @update:model-value="onFieldChanged" />
                        <div class="text-caption q-mb-xs">Checklist items (one per line):</div>
                        <q-input
                            v-model="checklistItemsText"
                            type="textarea"
                            dense
                            filled
                            autogrow
                            placeholder="Item 1&#10;Item 2&#10;Item 3"
                            @update:model-value="onChecklistItemsChanged"
                        />
                    </div>

                    <!-- CELLS options -->
                    <div v-else-if="localField.typeId === 'CELLS'" class="q-gutter-sm">
                        <div class="text-caption q-mb-xs">Cell fields:</div>
                        <div
                            v-for="(cell, cellIndex) in cellsEditorRows"
                            :key="cellIndex"
                            class="row q-gutter-xs q-mb-xs items-center"
                        >
                            <q-input
                                v-model="cell.key"
                                label="Field key"
                                dense
                                filled
                                class="col"
                                @update:model-value="onCellsEditorChanged"
                            />
                            <q-input
                                v-model="cell.label"
                                label="Label (optional)"
                                dense
                                filled
                                class="col"
                                @update:model-value="onCellsEditorChanged"
                            />
                            <q-select
                                v-model="cell.type"
                                :options="cellTypeOptions"
                                label="Type"
                                dense
                                filled
                                emit-value
                                map-options
                                style="min-width: 110px;"
                                @update:model-value="onCellsEditorChanged"
                            />
                            <q-input
                                v-model.number="cell.split"
                                label="Split"
                                dense
                                filled
                                type="number"
                                style="max-width: 70px;"
                                @update:model-value="onCellsEditorChanged"
                            />
                            <q-btn flat dense round icon="close" size="sm" color="negative" @click="removeCellRow(cellIndex)" />
                        </div>
                        <q-btn flat dense no-caps icon="add" label="Add cell" color="primary" size="sm" @click="addCellRow" />
                    </div>

                    <!-- MULTI options -->
                    <div v-else-if="localField.typeId === 'MULTI'" class="q-gutter-sm">
                        <div class="row q-gutter-sm">
                            <q-input v-model.number="localField.options.min" label="Min entries" dense filled type="number" class="col" @update:model-value="onFieldChanged" />
                            <q-input v-model.number="localField.options.max" label="Max entries" dense filled type="number" class="col" @update:model-value="onFieldChanged" />
                        </div>
                        <q-toggle v-model="localField.options.inline" label="Inline layout" dense size="sm" @update:model-value="onFieldChanged" />
                        <q-select
                            v-model="nestedFieldType"
                            :options="nestedFieldTypeOptions"
                            label="Nested field type"
                            dense
                            filled
                            emit-value
                            map-options
                            @update:model-value="onNestedFieldTypeChanged"
                        />
                        <!-- Nested CELLS editor -->
                        <div v-if="nestedFieldType === 'CELLS'" class="q-mt-sm">
                            <div class="text-caption q-mb-xs">Cell fields:</div>
                            <div
                                v-for="(cell, cellIndex) in multiCellsEditorRows"
                                :key="cellIndex"
                                class="row q-gutter-xs q-mb-xs items-center"
                            >
                                <q-input
                                    v-model="cell.key"
                                    label="Field key"
                                    dense
                                    filled
                                    class="col"
                                    @update:model-value="onMultiCellsEditorChanged"
                                />
                                <q-input
                                    v-model="cell.label"
                                    label="Label (optional)"
                                    dense
                                    filled
                                    class="col"
                                    @update:model-value="onMultiCellsEditorChanged"
                                />
                                <q-select
                                    v-model="cell.type"
                                    :options="cellTypeOptions"
                                    label="Type"
                                    dense
                                    filled
                                    emit-value
                                    map-options
                                    style="min-width: 110px;"
                                    @update:model-value="onMultiCellsEditorChanged"
                                />
                                <q-input
                                    v-model.number="cell.split"
                                    label="Split"
                                    dense
                                    filled
                                    type="number"
                                    style="max-width: 70px;"
                                    @update:model-value="onMultiCellsEditorChanged"
                                />
                                <q-btn flat dense round icon="close" size="sm" color="negative" @click="removeMultiCellRow(cellIndex)" />
                            </div>
                            <q-btn flat dense no-caps icon="add" label="Add cell" color="primary" size="sm" @click="addMultiCellRow" />
                        </div>
                        <!-- Nested STAR_RATING editor -->
                        <div v-if="nestedFieldType === 'STAR_RATING'" class="q-mt-sm q-gutter-sm">
                            <q-input
                                v-model.number="localField.options.field.options.count"
                                label="Star count"
                                dense
                                filled
                                type="number"
                                @update:model-value="onFieldChanged"
                            />
                            <q-toggle
                                v-model="localField.options.field.options.allowHalf"
                                label="Allow half stars"
                                dense
                                size="sm"
                                @update:model-value="onFieldChanged"
                            />
                        </div>
                    </div>

                    <!-- Raw JSON editor fallback -->
                    <div v-else class="q-gutter-sm">
                        <q-input
                            v-model="rawOptionsJson"
                            label="Options (JSON)"
                            type="textarea"
                            dense
                            filled
                            autogrow
                            :error="jsonError !== null"
                            :error-message="jsonError"
                            @update:model-value="onRawJsonChanged"
                        />
                    </div>

                    <!-- Comments option (available for all input fields) -->
                    <q-toggle
                        v-if="localField.typeId !== 'DISPLAY_TEXT'"
                        v-model="localField.options.comments"
                        label="Include comments field"
                        dense
                        size="sm"
                        class="q-mt-sm"
                        @update:model-value="onFieldChanged"
                    />
                </div>
            </q-slide-transition>
        </q-card-section>
    </q-card>
</template>

<script>
export default {
    name: "FormBuilderFieldEditor",
    props: {
        field: { type: Object, required: true },
        fieldTypes: { type: Array, default: () => [] },
        isFirst: { type: Boolean, default: false },
        isLast: { type: Boolean, default: false },
    },
    emits: ['update', 'move-up', 'move-down', 'remove'],
    data() {
        return {
            localField: this.cloneField(this.field),
            expanded: false,
            jsonError: null,
            rawOptionsJson: JSON.stringify(this.field.options || {}, null, 2),
            customOptionsText: '',
            checklistItemsText: '',
            cellsEditorRows: [],
            multiCellsEditorRows: [],
            selectPreset: 'custom',
            nestedFieldType: this.field.options?.field?.type || 'TEXT',
        };
    },
    computed: {
        /**
         * Base type family for the current field. Demographic SELECT_* variants
         * map to SELECT and DATETIME maps to DATE so they reuse the same editors.
         */
        baseTypeId() {
            const typeId = this.localField.typeId;
            if (typeof typeId !== 'string') return typeId;
            if (typeId.startsWith('SELECT_')) return 'SELECT';
            if (typeId === 'DATETIME') return 'DATE';
            return typeId;
        },
        fieldTypeOptions() {
            return this.fieldTypes.map(ft => ({ label: ft.id, value: ft.id }));
        },
        selectPresetOptions() {
            return [
                { label: 'Custom', value: 'custom' },
                { label: 'Yes / No', value: 'yesno' },
                { label: 'Yes / No / Other', value: 'oyesno' },
                { label: 'Likert Scale', value: 'likert' },
                { label: 'True / False', value: 'truefalse' },
            ];
        },
        nestedFieldTypeOptions() {
            return [
                { label: 'TEXT', value: 'TEXT' },
                { label: 'CELLS', value: 'CELLS' },
                { label: 'SELECT', value: 'SELECT' },
                { label: 'DATE', value: 'DATE' },
                { label: 'STAR_RATING', value: 'STAR_RATING' },
            ];
        },
        cellTypeOptions() {
            return [
                { label: 'Text', value: 'text' },
                { label: 'Email', value: 'email' },
                { label: 'Phone', value: 'tel' },
                { label: 'Number', value: 'number' },
                { label: 'Date', value: 'date' },
                { label: 'URL', value: 'url' },
                { label: 'Multiline', value: 'textarea' },
            ];
        },
        emojiOptions() {
            return [
                { label: 'Happy', value: 'happy' },
                { label: 'Excited', value: 'excited' },
                { label: 'Angry', value: 'angry' },
                { label: 'Sad', value: 'sad' },
                { label: 'Worried', value: 'worried' },
                { label: 'Overwhelmed', value: 'overwhelmed' },
            ];
        },
    },
    watch: {
        field: {
            handler(newVal) {
                this.localField = this.cloneField(newVal);
                this.syncDerivedState();
            },
            deep: true,
        },
    },
    mounted() {
        this.syncDerivedState();
    },
    methods: {
        cloneField(field) {
            return JSON.parse(JSON.stringify(field));
        },
        syncDerivedState() {
            const opts = this.localField.options || {};
            // Select preset
            if (this.baseTypeId === 'SELECT' || this.baseTypeId === 'RADIO') {
                if (typeof opts.options === 'string') {
                    if (opts.options === 'yesno') this.selectPreset = 'yesno';
                    else if (opts.options === 'oyesno') this.selectPreset = 'oyesno';
                    else if (opts.options === 'likert') this.selectPreset = 'likert';
                    else this.selectPreset = 'truefalse';
                    this.customOptionsText = '';
                } else if (Array.isArray(opts.options)) {
                    this.selectPreset = 'custom';
                    this.customOptionsText = (opts.options || []).map(o =>
                        typeof o === 'string' ? o : (o.value !== o.label ? `${o.value}|${o.label}` : o.label)
                    ).join('\n');
                }
            }
            // Checklist items
            if (this.localField.typeId === 'CHECKLIST') {
                this.checklistItemsText = (opts.items || []).join('\n');
            }
            // Cells fields
            if (this.localField.typeId === 'CELLS') {
                this.cellsEditorRows = (opts.fields || []).map(f => {
                    if (typeof f === 'string') {
                        return { key: f, label: '', type: 'text', split: 1 };
                    }
                    const [key, config] = Object.entries(f)[0] || ['', {}];
                    const c = (config && typeof config === 'object') ? config : {};
                    return { key, label: c.label || '', type: c.type || 'text', split: c.split || 1 };
                });
            }
            // Multi nested type
            if (this.localField.typeId === 'MULTI') {
                this.nestedFieldType = opts.field?.type || 'TEXT';
                if (this.nestedFieldType === 'CELLS') {
                    const nestedOpts = opts.field?.options || {};
                    this.multiCellsEditorRows = (nestedOpts.fields || []).map(f => {
                        if (typeof f === 'string') {
                            return { key: f, label: '', type: 'text', split: 1 };
                        }
                        const [key, config] = Object.entries(f)[0] || ['', {}];
                        const c = (config && typeof config === 'object') ? config : {};
                        return { key, label: c.label || '', type: c.type || 'text', split: c.split || 1 };
                    });
                } else {
                    this.multiCellsEditorRows = [];
                }
            }
        },
        onFieldChanged() {
            if (this.localField.typeId === 'DURATION' && !this.localField.options.units?.length) {
                delete this.localField.options.units;
            }
            this.$emit('update', JSON.parse(JSON.stringify(this.localField)));
        },
        onSelectPresetChanged(preset) {
            if (preset === 'custom') {
                this.localField.options.options = [];
                this.customOptionsText = '';
            } else {
                this.localField.options.options = preset;
            }
            this.onFieldChanged();
        },
        onCustomOptionsChanged(text) {
            const lines = text.split('\n').filter(l => l.trim());
            this.localField.options.options = lines.map(line => {
                if (line.includes('|')) {
                    const [value, ...labelParts] = line.split('|');
                    return { value: value.trim(), label: labelParts.join('|').trim() };
                }
                return line.trim();
            });
            this.onFieldChanged();
        },
        onChecklistItemsChanged(text) {
            this.localField.options.items = text.split('\n').filter(l => l.trim());
            this.onFieldChanged();
        },
        onCellsEditorChanged() {
            this.localField.options.fields = this.cellsEditorRows
                .filter(r => r.key.trim())
                .map(r => {
                    const hasOptions = (r.type && r.type !== 'text') || r.label || (r.split && r.split > 1);
                    if (!hasOptions) return r.key.trim();
                    const config = {};
                    if (r.type && r.type !== 'text') config.type = r.type;
                    if (r.label) config.label = r.label;
                    if (r.split && r.split > 1) config.split = r.split;
                    return { [r.key.trim()]: config };
                });
            this.onFieldChanged();
        },
        addCellRow() {
            this.cellsEditorRows.push({ key: '', label: '', type: 'text', split: 1 });
        },
        removeCellRow(index) {
            this.cellsEditorRows.splice(index, 1);
            this.onCellsEditorChanged();
        },
        onNestedFieldTypeChanged(type) {
            const defaults = type === 'STAR_RATING' ? { count: 5, allowHalf: false } : {};
            this.localField.options.field = { type, options: defaults };
            this.multiCellsEditorRows = [];
            this.onFieldChanged();
        },
        onMultiCellsEditorChanged() {
            if (!this.localField.options.field) {
                this.localField.options.field = { type: 'CELLS', options: {} };
            }
            if (!this.localField.options.field.options) {
                this.localField.options.field.options = {};
            }
            this.localField.options.field.options.fields = this.multiCellsEditorRows
                .filter(r => r.key.trim())
                .map(r => {
                    const hasOptions = (r.type && r.type !== 'text') || r.label || (r.split && r.split > 1);
                    if (!hasOptions) return r.key.trim();
                    const config = {};
                    if (r.type && r.type !== 'text') config.type = r.type;
                    if (r.label) config.label = r.label;
                    if (r.split && r.split > 1) config.split = r.split;
                    return { [r.key.trim()]: config };
                });
            this.onFieldChanged();
        },
        addMultiCellRow() {
            this.multiCellsEditorRows.push({ key: '', label: '', type: 'text', split: 1 });
        },
        removeMultiCellRow(index) {
            this.multiCellsEditorRows.splice(index, 1);
            this.onMultiCellsEditorChanged();
        },
        onRawJsonChanged(text) {
            try {
                this.localField.options = JSON.parse(text);
                this.jsonError = null;
                this.onFieldChanged();
            } catch (e) {
                this.jsonError = 'Invalid JSON';
            }
        },
    },
};
</script>

<style scoped>
.field-editor-card {
    border-left: 3px solid #1976d2;
}
</style>
